import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { Portfolio, Property, InspectedProperty } from '@financial-portfolio/shared/types';
import { portfolioService } from './PortfolioService';
import { useAuth } from './AuthContext';
import { db } from './firebase';

interface PortfolioContextType {
  portfolio: Portfolio | null;
  properties: Property[];
  inspections: InspectedProperty[];
  loading: boolean;
  error: string | null;
  loadPortfolio: () => Promise<void>;
  addProperty: (property: Property) => Promise<void>;
  updateProperty: (propertyId: string, updates: Partial<Property>) => Promise<void>;
  updatePortfolio: (updates: Partial<Portfolio>) => Promise<void>;
  deleteProperty: (propertyId: string) => Promise<void>;
  addInspection: (inspection: InspectedProperty) => Promise<void>;
  updateInspection: (inspectionId: string, updates: Partial<InspectedProperty>) => Promise<void>;
  deleteInspection: (inspectionId: string) => Promise<void>;
  clearError: () => void;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined);

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
}

interface PortfolioProviderProps {
  children: ReactNode;
}

export function PortfolioProvider({ children }: PortfolioProviderProps) {
  const { user, isAuthorized, currentAccountId, isReadOnly } = useAuth();
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 5;

  const properties = portfolio?.properties || [];
  const inspections = portfolio?.inspections || [];

  const clearError = useCallback(() => setError(null), []);

  const loadPortfolio = useCallback(async () => {
    if (!user || !isAuthorized) return;

    setLoading(true);
    setError(null);

    try {
      // Pass currentAccountId to getPortfolio
      const portfolioData = await portfolioService.getPortfolio(user.uid, currentAccountId || undefined);
      setPortfolio(portfolioData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  }, [user, isAuthorized, currentAccountId]);

  const addProperty = useCallback(async (property: Property) => {
    if (!user) throw new Error('User not authenticated');
    if (isReadOnly) throw new Error('You have read-only access');

    setLoading(true);
    setError(null);

    try {
      await portfolioService.addProperty(user.uid, property, currentAccountId || undefined);
      await loadPortfolio(); // Refresh the portfolio
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add property');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, currentAccountId, loadPortfolio]);

  const updateProperty = useCallback(async (propertyId: string, updates: Partial<Property>) => {
    if (!user) throw new Error('User not authenticated');
    if (isReadOnly) throw new Error('You have read-only access');

    setLoading(true);
    setError(null);

    try {
      await portfolioService.updateProperty(user.uid, propertyId, updates, currentAccountId || undefined);
      // Real-time listener will automatically update the portfolio
      // No need to manually reload
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update property');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, currentAccountId]);

  const deleteProperty = useCallback(async (propertyId: string) => {
    if (!user) throw new Error('User not authenticated');
    if (isReadOnly) throw new Error('You have read-only access');

    setLoading(true);
    setError(null);

    try {
      await portfolioService.deleteProperty(user.uid, propertyId, currentAccountId || undefined);
      await loadPortfolio(); // Refresh the portfolio
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete property');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, currentAccountId, loadPortfolio]);

  const updatePortfolio = useCallback(async (updates: Partial<Portfolio>) => {
    if (!user) return;
    if (isReadOnly) throw new Error('You have read-only access');

    setLoading(true);
    setError(null);

    try {
      await portfolioService.updatePortfolio(user.uid, updates, currentAccountId || undefined);
      // Real-time listener will automatically update the portfolio
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update portfolio');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, currentAccountId]);

  const addInspection = useCallback(async (inspection: InspectedProperty) => {
    if (!user) throw new Error('User not authenticated');
    if (isReadOnly) throw new Error('You have read-only access');

    setLoading(true);
    try {
      await portfolioService.addInspection(user.uid, inspection, currentAccountId || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add inspection');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, currentAccountId]);

  const updateInspection = useCallback(async (inspectionId: string, updates: Partial<InspectedProperty>) => {
    if (!user) throw new Error('User not authenticated');
    if (isReadOnly) throw new Error('You have read-only access');

    setLoading(true);
    try {
      await portfolioService.updateInspection(user.uid, inspectionId, updates, currentAccountId || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update inspection');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, currentAccountId]);

  const deleteInspection = useCallback(async (inspectionId: string) => {
    if (!user) throw new Error('User not authenticated');
    if (isReadOnly) throw new Error('You have read-only access');

    setLoading(true);
    try {
      await portfolioService.deleteInspection(user.uid, inspectionId, currentAccountId || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete inspection');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user, currentAccountId]);

  // Set up real-time listener for portfolio updates
  useEffect(() => {
    if (!user || !isAuthorized || !user.email) {
      setPortfolio(null);
      return;
    }

    console.log('[PortfolioContext] Setting up real-time listener for user:', user.uid);
    console.log('[PortfolioContext] User email:', user.email);
    console.log('[PortfolioContext] isAuthorized:', isAuthorized);
    setLoading(true);

    let unsubscribe: (() => void) | null = null;
    const loadingTimeout = setTimeout(() => {
      console.error('[PortfolioContext] Loading timeout - portfolio listener not responding');
      setError('Loading timeout - please refresh the page');
      setLoading(false);
    }, 10000);

    // Async function to get account ID and set up listener
    const setupListener = async () => {
      // Ensure any existing listener is cleaned up before setting up a new one
      if (unsubscribe) {
        console.log('[PortfolioContext] Cleaning up existing listener before retry/re-setup');
        unsubscribe();
        unsubscribe = null;
      }

      try {
        let accountId = currentAccountId;

        // Fallback: Get the user's account ID from user_accounts collection if not in context
        if (!accountId) {
          console.warn('[PortfolioContext] User not assigned to any account - searching for account info');
          const userAccountInfo = await portfolioService.getUserAccount(user.email!);
          accountId = userAccountInfo?.accountId || null;
          console.log('[PortfolioContext] Fallback account lookup result:', accountId);
        }

        if (!accountId) {
          console.warn('[PortfolioContext] User still not assigned to any account after fallback');
          clearTimeout(loadingTimeout);
          setError('Account assignment required. Please contact your administrator.');
          setLoading(false);
          return;
        }

        console.log('[PortfolioContext] User assigned to account:', accountId);

        // Set up real-time listener for the account portfolio
        const portfolioRef = doc(db, 'account_portfolios', accountId);
        console.log('[PortfolioContext] Portfolio ref path:', portfolioRef.path);

        unsubscribe = onSnapshot(
          portfolioRef,
          (snapshot) => {
            clearTimeout(loadingTimeout);
            retryCountRef.current = 0; // Reset retry count on success

            console.log('[PortfolioContext] Snapshot received:', {
              exists: snapshot.exists(),
              id: snapshot.id,
            });

            if (snapshot.exists()) {
              const data = snapshot.data();
              // Portfolio is nested under 'portfolio' field (vanilla JS structure)
              const portfolioData = data['portfolio'] as Portfolio;

              if (portfolioData) {
                console.log('[PortfolioContext] Portfolio updated from Firestore:', {
                  propertiesCount: portfolioData.properties?.length || 0,
                  properties: portfolioData.properties?.map(p => ({
                    id: p.id,
                    address: p.address,
                    hasImage: !!p.imageUrl,
                  })),
                });
                setPortfolio(portfolioData);
              } else {
                console.warn('[PortfolioContext] Document exists but no portfolio field found');
                setPortfolio({
                  id: accountId,
                  userId: user.uid,
                  properties: [],
                  otherAssets: {},
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                });
              }
              setLoading(false);
            } else {
              console.log('[PortfolioContext] No portfolio document found - setting empty');
              setPortfolio({
                id: accountId,
                userId: user.uid,
                properties: [],
                otherAssets: {},
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });
              setLoading(false);
            }
          },
          (error) => {
            clearTimeout(loadingTimeout);
            console.error('[PortfolioContext] Error in portfolio listener:', error);
            console.error('[PortfolioContext] Error code:', error.code);
            console.error('[PortfolioContext] Error message:', error.message);

            // Handle transient permission errors for new users
            if (error.code === 'permission-denied' || error.message.includes('permissions')) {
              if (retryCountRef.current < MAX_RETRIES) {
                retryCountRef.current++;
                const delay = Math.pow(2, retryCountRef.current) * 1000;
                console.warn(`[PortfolioContext] Permission denied (attempt ${retryCountRef.current}/${MAX_RETRIES}). Retrying in ${delay}ms...`);

                setTimeout(() => {
                  // Only retry if still in valid auth state and subscribed
                  if (user && isAuthorized) {
                    console.log('[PortfolioContext] Executing scheduled retry...');
                    setupListener();
                  }
                }, delay);
                return;
              } else {
                console.error('[PortfolioContext] Max retries reached for permission errors');
              }
            }

            setError(`Failed to load portfolio: ${error.message}`);
            setLoading(false);
          }
        );
      } catch (error) {
        clearTimeout(loadingTimeout);
        console.error('[PortfolioContext] Error setting up listener:', error);
        setError('Failed to set up portfolio listener');
        setLoading(false);
      }
    };

    retryCountRef.current = 0;
    setupListener();

    return () => {
      clearTimeout(loadingTimeout);
      if (unsubscribe) {
        console.log('[PortfolioContext] Cleaning up real-time listener');
        unsubscribe();
      }
    };
  }, [user?.uid, isAuthorized, currentAccountId]);

  const value = {
    portfolio,
    properties,
    inspections,
    loading,
    error,
    loadPortfolio,
    addProperty,
    updateProperty,
    updatePortfolio,
    deleteProperty,
    addInspection,
    updateInspection,
    deleteInspection,
    clearError,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
}